import type { CreateBookmarkRequest, MovieList } from '@zyx/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApi } from '@/hooks/use-connection';
import { formatRuntime } from '@/lib/format';
import { queryKeys } from '@/lib/query-keys';

function failed(error: unknown) {
  toast.error(error instanceof Error ? error.message : 'The change was not saved.');
}

/** `enabled: false` waits until asked, so a grid of cards does not load the lists for each. */
export function useLists(options: { readonly enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.lists(), queryFn: () => api.listLists(), enabled: options.enabled });
}

export function useList(listId: number) {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.list(listId), queryFn: () => api.getList(listId) });
}

/** Lists live under `movies`, so this refreshes every card's marks and every list's count. */
function useRefreshMovies() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: queryKeys.movies() });
}

export function useCreateList() {
  const api = useApi();
  const refresh = useRefreshMovies();
  return useMutation({
    mutationFn: async (name: string): Promise<MovieList> => (await api.createList(name)).list,
    onSuccess: refresh,
    onError: failed,
  });
}

export function useRenameList(listId: number) {
  const api = useApi();
  const refresh = useRefreshMovies();
  return useMutation({ mutationFn: (name: string) => api.renameList(listId, name), onSuccess: refresh, onError: failed });
}

/**
 * `onDeleted` runs first (to leave the list's page), and the list's own query is dropped
 * before the refresh, so nothing asks for the list that is gone and shows a 404.
 */
export function useDeleteList(listId: number, onDeleted: () => void) {
  const api = useApi();
  const client = useQueryClient();
  const refresh = useRefreshMovies();
  return useMutation({
    mutationFn: () => api.deleteList(listId),
    onSuccess: async () => {
      onDeleted();
      client.removeQueries({ queryKey: queryKeys.list(listId) });
      await refresh();
    },
    onError: failed,
  });
}

export type ListChange = { readonly listId: number; readonly code: string; readonly on: true; readonly after?: string | null } | { readonly listId: number; readonly code: string; readonly on: false };

/** Puts a movie on a list, moves it within one, or takes it off. */
export function useChangeList() {
  const api = useApi();
  const refresh = useRefreshMovies();
  return useMutation({
    mutationFn: (change: ListChange) => (change.on ? api.putListMovie({ listId: change.listId, code: change.code, after: change.after }) : api.removeListMovie(change.listId, change.code)),
    onSuccess: refresh,
    onError: failed,
  });
}

/** One movie's bookmarks, or with no code every bookmark, newest first. */
export function useBookmarks(code?: string) {
  const api = useApi();
  return useQuery({
    queryKey: code === undefined ? queryKeys.bookmarks() : queryKeys.movieBookmarks(code),
    queryFn: () => api.listBookmarks({ code }),
  });
}

export function useCreateBookmark() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateBookmarkRequest) => api.createBookmark(request),
    onSuccess: async ({ bookmark }) => {
      toast.success(`Bookmarked ${bookmark.code} at ${formatRuntime(bookmark.positionS) ?? '0:00'}.`);
      await client.invalidateQueries({ queryKey: queryKeys.bookmarks() });
    },
    onError: failed,
  });
}

export function useUpdateBookmark() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (change: { readonly bookmarkId: number; readonly label: string | null }) => api.updateBookmark(change),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.bookmarks() }),
    onError: failed,
  });
}

export function useDeleteBookmark() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (bookmarkId: number) => api.deleteBookmark(bookmarkId),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.bookmarks() }),
    onError: failed,
  });
}
